/**
 * BreadcrumbBar Component (Group Sub-Timeline feature)
 *
 * Navigation bar displayed above timeline when editing a group's internal tools.
 * Shows the navigation path: "Main Timeline > {Group Name}"
 *
 * Features:
 * - "Main Timeline" is clickable to exit group edit mode
 * - Group name is editable inline (click to edit, Enter to save, Escape to cancel)
 * - Dark background to visually separate from timeline
 * - Consistent 32px height
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Home, Pencil } from 'lucide-react';

interface BreadcrumbBarProps {
  /** Name of the group currently being edited */
  groupName: string;

  /** Callback to exit group edit mode and return to main timeline */
  onExitEditMode: () => void;

  /** Callback to rename the group. Returns true if rename succeeded. */
  onRenameGroup?: (newName: string) => boolean;
}

export function BreadcrumbBar({
  groupName,
  onExitEditMode,
  onRenameGroup,
}: BreadcrumbBarProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(groupName);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleMainTimelineClick = useCallback(() => {
    onExitEditMode();
  }, [onExitEditMode]);

  const startEditing = useCallback(() => {
    if (!onRenameGroup) return;
    setEditValue(groupName);
    setIsEditing(true);
  }, [groupName, onRenameGroup]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditValue(groupName);
  }, [groupName]);

  const commitEditing = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== groupName && onRenameGroup) {
      const success = onRenameGroup(trimmed);
      if (!success) {
        setEditValue(groupName);
      }
    }
    setIsEditing(false);
  }, [editValue, groupName, onRenameGroup]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEditing();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEditing();
      }
      e.stopPropagation();
    },
    [commitEditing, cancelEditing]
  );

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <div
      className="flex items-center h-8 px-3 gap-2"
      style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
      }}
      data-testid="group-edit-breadcrumb"
    >
      {/* Main Timeline link */}
      <button
        onClick={handleMainTimelineClick}
        className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors"
        data-testid="breadcrumb-main-timeline"
      >
        <Home className="h-3.5 w-3.5" />
        <span>Main Timeline</span>
      </button>

      {/* Separator */}
      <ChevronRight className="h-4 w-4 text-gray-500" />

      {/* Current group name - editable inline */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEditing}
          onKeyDown={handleKeyDown}
          className="text-sm font-medium text-white bg-transparent border border-gray-500 rounded px-1.5 py-0.5 outline-none focus:border-purple-400"
          style={{ maxWidth: '200px' }}
          maxLength={50}
          data-testid="breadcrumb-group-name-input"
        />
      ) : (
        <button
          onClick={startEditing}
          className="flex items-center gap-1.5 text-sm font-medium text-white hover:text-purple-300 transition-colors group"
          data-testid="breadcrumb-group-name"
          title={onRenameGroup ? 'Click to rename group' : undefined}
          disabled={!onRenameGroup}
        >
          <span>{groupName}</span>
          {onRenameGroup && (
            <Pencil className="h-3 w-3 text-gray-500 group-hover:text-purple-300 transition-colors" />
          )}
        </button>
      )}

      {/* Edit mode indicator badge */}
      <span
        className="ml-auto text-xs px-2 py-0.5 rounded"
        style={{
          backgroundColor: '#8b5cf6',
          color: 'white',
        }}
      >
        Editing
      </span>
    </div>
  );
}
